import { useEffect } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { useUi } from "../state/ui";
import { PlusIcon, MusicNoteIcon } from "./icons";
import { useT } from "../i18n";
import { tempoChanges } from "../core/grid/fixedGrid";
import { KeyChip, KeySheet } from "./KeyControl";

// Song header: cover · title · key/tempo/bars chips, plus "다른 곡" to reopen the (collapsed) input.
// Replaces the v1 big-number tempo/key cards so the score starts on the first screen.
export function AnalysisPanel() {
  const c = useControllers();
  const t = useT();
  const analysis = useProjectStore((s) => s.analysis);
  const headerTempo = useProjectStore((s) => s.headerTempo);
  const source = useProjectStore((s) => s.source);
  const fallbackTitle = useProjectStore((s) => s.title);
  const bars = useProjectStore((s) => s.chartMeasures.length);
  const setInputOpen = useUi((s) => s.setInputOpen);
  const tempoGrid = useProjectStore((s) => s.tempoGrid);
  const displayTitle = useProjectStore((s) => s.displayTitle);

  // the YouTube player reports the video title only once it's ready — poll briefly (getTitle checks
  // the video id, so a previous song's title never leaks in)
  useEffect(() => {
    if (source.kind !== "youtube") return;
    let n = 0;
    const id = window.setInterval(() => {
      const title = c.youtube.getTitle?.(source.videoId) || "";
      if (title) useProjectStore.getState().setDisplayTitle(title);
      if (title || ++n > 40) window.clearInterval(id);
    }, 250);
    return () => window.clearInterval(id);
  }, [source, c]);

  if (!analysis) return null;

  const title = displayTitle || fallbackTitle;
  const videoId = source.kind === "youtube" ? source.videoId : null;
  // songs with real tempo changes show the range ("96–120 BPM"); markers sit on the chord grid
  const changes = tempoChanges(tempoGrid);
  const bpms = changes.map((c) => c.bpm);
  const tempoLabel = changes.length > 1 ? `${Math.min(...bpms)}–${Math.max(...bpms)}` : (headerTempo ?? "—");

  return (
    <section className="section song-head">
      <div className="song-cover" aria-hidden="true">
        {videoId ? <img src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`} alt="" loading="lazy" /> : <MusicNoteIcon size={26} />}
      </div>
      <div className="song-meta">
        <h1 className="song-title">{title}</h1>
        <div className="song-chips">
          <KeyChip />
          <span className="chip"><b>{tempoLabel}</b> BPM</span>
          <span className="chip">{t("song.bars", { n: bars })}</span>
        </div>
      </div>
      <button className="new-song" onClick={() => { setInputOpen(true); document.querySelector(".scroll-area")?.scrollTo({ top: 0 }); }}>
        <PlusIcon size={14} /><span>{t("song.new")}</span>
      </button>
      <KeySheet />
    </section>
  );
}
